import { Chip, Stack, Tooltip, Typography } from "@mui/material"
import { fDateTime } from "minimals-template/components/utils/formatTime"

/**
 * A tripped breaker is invisible from inside the tenant until someone notices SCIM has stopped
 * (SNAG-001). Surfacing it here, across every client at once, is the point of this column — so
 * the tripped state is a filled error chip with the reason and trip time attached, not a subtle
 * status word.
 *
 * Three distinct states, deliberately not two:
 *   null                 SCIM is switched off for this tenant — there is no breaker to report.
 *   { unavailable: true} the breaker could not be read (Redis blip). NOT the same as the above:
 *                        an unknown state that renders as "n/a" is exactly the silent-failure
 *                        mode this whole page exists to prevent.
 *   otherwise            live state — armed, disabled, or tripped.
 */
export function EstateBreakerCell({ breaker }) {
    if (!breaker) {
        return (
            <Typography variant="caption" color="text.secondary">
                n/a
            </Typography>
        )
    }

    return (
        <Stack spacing={0.25} alignItems="flex-start">
            <Tooltip title={breaker.reason || ""}>
                <Chip
                    size="small"
                    variant={variant()}
                    color={colour()}
                    label={label()}
                    data-cy="sso-estate-breaker-chip"
                />
            </Tooltip>
            <Typography variant="caption" color={detailColour()}>
                {detail()}
            </Typography>
        </Stack>
    )

    // The window counts are zeroed when the read failed, so showing them would assert "0
    // deprovisions" for a tenant we know nothing about. Say so instead.
    function detail() {
        if (breaker.unavailable) return "State could not be read"
        if (breaker.tripped && breaker.trippedAt) return fDateTime(breaker.trippedAt)
        return `${breaker.deprovisionsInWindow || 0} in ${breaker.windowMinutes || 0}m`
    }

    function detailColour() {
        if (breaker.unavailable) return "warning.main"
        if (breaker.tripped && breaker.trippedAt) return "error.main"
        return "text.secondary"
    }

    function label() {
        if (breaker.unavailable) return "Unknown"
        if (breaker.tripped) return "Tripped"
        if (breaker.disabled) return "Disabled"
        return "Armed"
    }

    function colour() {
        if (breaker.unavailable) return "warning"
        if (breaker.tripped) return "error"
        if (breaker.disabled) return "warning"
        return "success"
    }

    function variant() {
        return breaker.tripped ? "filled" : "outlined"
    }
}
